import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import {
  getAllLiveItems,
  updateLiveItem,
  bulkUpdateLiveItems,
  deleteLiveItem,
  getGooglePrice,
  setGooglePrice,
  bulkSetGooglePrice,
  getAllGooglePrices,
  getApplePrice,
  setApplePrice,
  bulkSetApplePrice,
  getAllApplePrices
} from '../../services/liveItemService'
import withAdminAuth from './withAdminAuth'
import classes from './live_wallpaper_manager.module.css'

const PAGE_SIZE_OPTIONS = [50, 100, 150, 200]

// Base currencies the Google Play price can be set in. The chosen currency's
// home region gets exactly the amount entered; Google converts the rest.
// Mirrors CURRENCIES in backend/src/services/googlePlay.service.js.
const CURRENCIES = {
  USD: { symbol: '$', step: '0.01', decimals: 2, placeholder: '1.99', homeLabel: '美國' },
  TWD: { symbol: 'NT$', step: '1', decimals: 0, placeholder: '60', homeLabel: '台灣' }
}
const CURRENCY_CODES = Object.keys(CURRENCIES)

// Max SKUs the bulk endpoint accepts per request (backend BULK_MAX).
const BULK_CHUNK = 25

// Apple's bulk cap is lower (backend appStore BULK_MAX) — each SKU costs more
// App Store Connect round-trips than Google.
const APPLE_BULK_CHUNK = 10

function formatDate (value) {
  if (!value) return '—'
  const d = new Date(value)
  if (isNaN(d.getTime())) return '—'
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

// Format a price for the Google column, e.g. (1.99,'USD') -> "$1.99",
// (60,'TWD') -> "NT$60". Returns null when the SKU has no price in that currency.
function formatMoney (n, code) {
  if (n == null) return null
  const { symbol, decimals } = CURRENCIES[code]
  return `${symbol}${decimals === 0 ? Math.round(n) : Number(n).toFixed(2)}`
}

// Reject fractional amounts for zero-decimal currencies before hitting Google.
function priceError (raw, code) {
  const n = Number(raw)
  if (raw === '' || !Number.isFinite(n) || n < 0) {
    return `請輸入有效的${code === 'TWD' ? '台幣' : '美金'}價格，例如 ${CURRENCIES[code].placeholder}`
  }
  if (CURRENCIES[code].decimals === 0 && !Number.isInteger(n)) {
    return `${code} 不支援小數，請輸入整數（例 ${CURRENCIES[code].placeholder}）`
  }
  return null
}

function Bool ({ value }) {
  return (
    <span className={`${classes.badge} ${value ? classes.badgeYes : classes.badgeNo}`}>
      {value ? 'YES' : 'no'}
    </span>
  )
}
Bool.propTypes = { value: PropTypes.bool }

// Google column cell: every supported currency stacked, or "Add" when the SKU
// has no one-time product in Play Console yet.
function GooglePriceLabel ({ prices }) {
  const lines = CURRENCY_CODES
    .map((code) => formatMoney(prices?.[code], code))
    .filter(Boolean)

  if (lines.length === 0) return 'Add'
  return (
    <span className={classes.priceStack}>
      {lines.map((line) => <span key={line}>{line}</span>)}
    </span>
  )
}
GooglePriceLabel.propTypes = { prices: PropTypes.object }

// Apple column cell: the USA price (Apple derives every other region from it),
// or "Add" when the itemId has no in-app purchase in App Store Connect yet.
// Non-APPROVED products carry the ASC state as a tooltip (e.g. READY_TO_SUBMIT
// = created but never submitted for review with an app version).
function ApplePriceLabel ({ info }) {
  const price = info?.USD
  if (price == null) return 'Add'
  const pending = info.state && info.state !== 'APPROVED'
  return (
    <span className={classes.priceStack} title={info.state || undefined}>
      <span>{`$${Number(price).toFixed(2)}${pending ? ' ⏳' : ''}`}</span>
    </span>
  )
}
ApplePriceLabel.propTypes = { info: PropTypes.object }

// Column definitions. `sortable` columns are clickable in the header; `get`
// overrides the default `it[key]` accessor; `type` drives the comparator.
const COLUMNS = [
  { key: 'itemId', label: 'itemId', type: 'string', sortable: true },
  { key: 'thumbnail', label: 'thumbnail', sortable: false },
  { key: 'sortPriority', label: 'sortPriority', type: 'number', sortable: true },
  { key: 'createdAt', label: 'created date', type: 'date', sortable: true },
  { key: 'name', label: 'name', type: 'string', sortable: true },
  { key: 'tags', label: 'tag', type: 'string', sortable: true, get: (it) => (it.tags || []).join(', ') },
  { key: 'price', label: 'price', type: 'number', sortable: true },
  { key: 'flowerCost', label: 'flowerCost', type: 'number', sortable: true },
  { key: 'freeTrialDays', label: 'freeTrialDays', type: 'number', sortable: true },
  { key: 'freeOnDaysOfWeek', label: 'freeOnDaysOfWeek', type: 'string', sortable: false, get: (it) => (it.freeOnDaysOfWeek || []).join(',') },
  { key: 'maxTrialDays', label: 'maxTrialDays', type: 'number', sortable: true },
  { key: 'freeDownload', label: 'freeDownload', type: 'bool', sortable: true },
  { key: 'isVisible', label: 'isVisible', type: 'bool', sortable: true },
  { key: 'googlePrice', label: 'Google', sortable: false },
  { key: 'applePrice', label: 'Apple', sortable: false },
  { key: 'edit', label: 'edit', sortable: false },
  { key: 'delete', label: 'delete', sortable: false }
]

const COL_BY_KEY = COLUMNS.reduce((acc, c) => ({ ...acc, [c.key]: c }), {})

// ISO weekday numbers (1=Mon … 7=Sun) for the "free on days of week" checkboxes.
const WEEKDAYS = [
  { n: 1, label: 'Mon' },
  { n: 2, label: 'Tue' },
  { n: 3, label: 'Wed' },
  { n: 4, label: 'Thu' },
  { n: 5, label: 'Fri' },
  { n: 6, label: 'Sat' },
  { n: 7, label: 'Sun' }
]

// Columns hidden by default; the admin can re-enable them via the toggle bar.
const DEFAULT_HIDDEN_COLS = ['itemId', 'name', 'price']

function buildDefaultVisibility () {
  return COLUMNS.reduce((acc, c) => ({ ...acc, [c.key]: !DEFAULT_HIDDEN_COLS.includes(c.key) }), {})
}

// Build a comparator for the given column + direction.
function compareBy (col, dir) {
  const mul = dir === 'asc' ? 1 : -1
  const get = col.get || ((it) => it[col.key])
  return (a, b) => {
    let va = get(a)
    let vb = get(b)
    if (col.type === 'date') { va = new Date(va || 0).getTime(); vb = new Date(vb || 0).getTime() } else if (col.type === 'number') { va = Number(va || 0); vb = Number(vb || 0) } else if (col.type === 'bool') { va = va ? 1 : 0; vb = vb ? 1 : 0 } else { va = String(va ?? '').toLowerCase(); vb = String(vb ?? '').toLowerCase() }
    if (va < vb) return -1 * mul
    if (va > vb) return 1 * mul
    return 0
  }
}

function LiveWallpaperManager () {
  const [items, setItems] = useState(null)
  const [loading, setLoading] = useState(true)
  const [googlePrices, setGooglePrices] = useState({}) // { [itemId]: number|null }
  const [googleLoading, setGoogleLoading] = useState(true)
  const [applePrices, setApplePrices] = useState({}) // { [itemId]: { USD, state } }
  const [appleLoading, setAppleLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [modal, setModal] = useState(null) // { mode: 'edit'|'delete'|'price', item }
  const [sort, setSort] = useState({ key: 'createdAt', dir: 'desc' }) // newest first by default
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0])
  const [visibleCols, setVisibleCols] = useState(buildDefaultVisibility) // { [key]: bool }
  const [selectedIds, setSelectedIds] = useState(() => new Set()) // itemIds picked for bulk edit
  const [showBulk, setShowBulk] = useState(false)
  const [showBulkPrice, setShowBulkPrice] = useState(false)
  const [showBulkApplePrice, setShowBulkApplePrice] = useState(false)

  const toggleCol = (key) => setVisibleCols((prev) => ({ ...prev, [key]: !prev[key] }))
  const shownColumns = COLUMNS.filter((c) => visibleCols[c.key])

  const toggleSelect = (itemId) => setSelectedIds((prev) => {
    const next = new Set(prev)
    if (next.has(itemId)) next.delete(itemId)
    else next.add(itemId)
    return next
  })
  const clearSelection = () => setSelectedIds(new Set())

  const load = async () => {
    setLoading(true)
    try {
      const data = await getAllLiveItems()
      setItems(Array.isArray(data) ? data : [])
    } catch (err) {
      console.error(err)
      setItems([])
    } finally {
      setLoading(false)
    }
  }

  // Bulk-load every Google Play one-time price so the Google column can show
  // the current USD price (or "Add" when a SKU has no one-time product yet).
  const loadGooglePrices = async () => {
    setGoogleLoading(true)
    try {
      const prices = await getAllGooglePrices()
      setGooglePrices(prices || {})
    } catch (err) {
      console.error('Failed to load Google prices:', err)
    } finally {
      setGoogleLoading(false)
    }
  }

  // Same bulk prefill for the Apple column. Errors degrade to "Add" cells (the
  // whole page must stay usable while ASC credentials are not configured yet).
  const loadApplePrices = async () => {
    setAppleLoading(true)
    try {
      const prices = await getAllApplePrices()
      setApplePrices(prices || {})
    } catch (err) {
      console.error('Failed to load Apple prices:', err)
    } finally {
      setAppleLoading(false)
    }
  }

  useEffect(() => { load() }, [])
  useEffect(() => { loadGooglePrices() }, [])
  useEffect(() => { loadApplePrices() }, [])

  // Filter by the search box, then sort by the active column/direction.
  const sorted = useMemo(() => {
    if (!items) return []
    const q = search.trim().toLowerCase()
    const filtered = q
      ? items.filter((it) =>
        String(it.itemId).toLowerCase().includes(q) ||
          String(it.name || '').toLowerCase().includes(q))
      : items
    const col = COL_BY_KEY[sort.key] || COL_BY_KEY.createdAt
    return [...filtered].sort(compareBy(col, sort.dir))
  }, [items, search, sort])

  const onSort = (col) => {
    if (!col.sortable) return
    setSort((prev) => prev.key === col.key
      ? { key: col.key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key: col.key, dir: 'asc' })
  }

  // Reset to first page whenever the filter, sort, or page size changes.
  useEffect(() => { setPage(0) }, [search, sort, pageSize])

  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize))
  const safePage = Math.min(page, pageCount - 1)
  const pageRows = sorted.slice(safePage * pageSize, safePage * pageSize + pageSize)

  const pageIds = pageRows.map((it) => it.itemId)
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id))
  const toggleSelectAllPage = () => setSelectedIds((prev) => {
    const next = new Set(prev)
    if (allPageSelected) pageIds.forEach((id) => next.delete(id))
    else pageIds.forEach((id) => next.add(id))
    return next
  })
  const selectedItems = (items || []).filter((it) => selectedIds.has(it.itemId))

  return (
    <div className={classes.page}>
      <div className={classes.inner}>
        <h2 className={classes.title}>DIY Live Wallpaper Manager</h2>
        <p className={classes.subtitle}>Admin only · 管理所有 DIY live wallpaper（新增/修改/刪除 · Google Play / App Store 定價）</p>

        <div className={classes.toolbar}>
          <input
            className={classes.search}
            placeholder="Search by itemId or name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select
            className={classes.pageSizeSelect}
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            aria-label="Rows per page"
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>{n} 筆/頁</option>
            ))}
          </select>
          <span className={classes.count}>{sorted.length} item(s)</span>
          {selectedIds.size > 0 && (
            <>
              <button className={classes.bulkBtn} onClick={() => setShowBulk(true)}>
                Edit selected ({selectedIds.size})
              </button>
              <button className={classes.bulkBtn} onClick={() => setShowBulkPrice(true)}>
                Google price ({selectedIds.size})
              </button>
              <button className={classes.bulkBtn} onClick={() => setShowBulkApplePrice(true)}>
                Apple price ({selectedIds.size})
              </button>
              <button className={classes.colBarBtn} onClick={clearSelection}>
                清除選取
              </button>
            </>
          )}
        </div>

        <div className={classes.colBar}>
          <span className={classes.colBarLabel}>顯示欄位：</span>
          {COLUMNS.map((col) => (
            <label key={col.key} className={classes.colToggle}>
              <input
                type="checkbox"
                checked={!!visibleCols[col.key]}
                onChange={() => toggleCol(col.key)}
              />
              {col.label}
            </label>
          ))}
          <button className={classes.colBarBtn} onClick={() => setVisibleCols(buildDefaultVisibility())}>
            重設
          </button>
        </div>

        {loading
          ? (
          <div className={classes.loading}>Loading live wallpapers…</div>
            )
          : (
          <>
            <div className={classes.tableWrap}>
              <table className={classes.table}>
                <thead>
                  <tr>
                    <th className={classes.selectCell}>
                      <input
                        type="checkbox"
                        checked={allPageSelected}
                        onChange={toggleSelectAllPage}
                        aria-label="Select all on this page"
                      />
                    </th>
                    {shownColumns.map((col) => (
                      <th
                        key={col.key}
                        className={col.sortable ? classes.sortable : undefined}
                        onClick={col.sortable ? () => onSort(col) : undefined}
                      >
                        {col.label}
                        {col.sortable && sort.key === col.key && (
                          <span className={classes.sortArrow}>{sort.dir === 'asc' ? ' ▲' : ' ▼'}</span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pageRows.length === 0 && (
                    <tr><td colSpan={shownColumns.length + 1} className={classes.empty}>No live wallpapers.</td></tr>
                  )}
                  {pageRows.map((it) => (
                    <tr key={it.itemId} className={selectedIds.has(it.itemId) ? classes.rowSelected : undefined}>
                      <td className={classes.selectCell}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(it.itemId)}
                          onChange={() => toggleSelect(it.itemId)}
                          aria-label={`Select ${it.itemId}`}
                        />
                      </td>
                      {visibleCols.itemId && <td className={classes.idCell}>{it.itemId}</td>}
                      {visibleCols.thumbnail && (
                        <td>
                          {it.thumbnailUrl
                            ? <img className={classes.preview} src={it.thumbnailUrl} alt={it.itemId} loading="lazy" />
                            : <span className={classes.noPreview} />}
                        </td>
                      )}
                      {visibleCols.sortPriority && <td className={classes.num}>{it.sortPriority ?? 0}</td>}
                      {visibleCols.createdAt && <td className={classes.date}>{formatDate(it.createdAt)}</td>}
                      {visibleCols.name && <td>{it.name}</td>}
                      {visibleCols.tags && <td className={classes.tagList}>{(it.tags || []).join(', ') || '—'}</td>}
                      {visibleCols.price && <td className={classes.num}>${Number(it.price ?? 0).toFixed(2)}</td>}
                      {visibleCols.flowerCost && <td className={classes.num}>{it.flowerCost ?? '—'}</td>}
                      {visibleCols.freeTrialDays && <td className={classes.num}>{it.freeTrialDays ?? '—'}</td>}
                      {visibleCols.freeOnDaysOfWeek && <td className={classes.num}>{(it.freeOnDaysOfWeek && it.freeOnDaysOfWeek.length) ? it.freeOnDaysOfWeek.join(',') : '—'}</td>}
                      {visibleCols.maxTrialDays && <td className={classes.num}>{it.maxTrialDays ?? '—'}</td>}
                      {visibleCols.freeDownload && <td><Bool value={!!it.freeDownload} /></td>}
                      {visibleCols.isVisible && <td><Bool value={!!it.isVisible} /></td>}
                      {visibleCols.googlePrice && (
                        <td>
                          <button className={classes.priceBtn} onClick={() => setModal({ mode: 'price', item: it })}>
                            {googleLoading ? '…' : <GooglePriceLabel prices={googlePrices[it.itemId]} />}
                          </button>
                        </td>
                      )}
                      {visibleCols.applePrice && (
                        <td>
                          <button className={classes.priceBtn} onClick={() => setModal({ mode: 'applePrice', item: it })}>
                            {appleLoading ? '…' : <ApplePriceLabel info={applePrices[it.itemId]} />}
                          </button>
                        </td>
                      )}
                      {visibleCols.edit && (
                        <td>
                          <button className={classes.actionBtn} onClick={() => setModal({ mode: 'edit', item: it })}>
                            Edit
                          </button>
                        </td>
                      )}
                      {visibleCols.delete && (
                        <td>
                          <button
                            className={`${classes.actionBtn} ${classes.deleteBtn}`}
                            onClick={() => setModal({ mode: 'delete', item: it })}
                          >
                            Delete
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className={classes.pager}>
              <button
                className={classes.pagerBtn}
                disabled={safePage <= 0}
                onClick={() => setPage(safePage - 1)}
              >
                ← 上一頁
              </button>
              <span className={classes.pageInfo}>Page {safePage + 1} / {pageCount}</span>
              <button
                className={classes.pagerBtn}
                disabled={safePage >= pageCount - 1}
                onClick={() => setPage(safePage + 1)}
              >
                下一頁 →
              </button>
            </div>
          </>
            )}
      </div>

      {modal && (
        <ItemModal
          modal={modal}
          onClose={() => setModal(null)}
          onDone={async () => { setModal(null); await Promise.all([load(), loadGooglePrices(), loadApplePrices()]) }}
        />
      )}

      {showBulk && (
        <BulkEditModal
          items={selectedItems}
          onClose={() => setShowBulk(false)}
          onDone={async () => { setShowBulk(false); clearSelection(); await load() }}
        />
      )}

      {showBulkPrice && (
        <BulkPriceModal
          items={selectedItems}
          onClose={() => setShowBulkPrice(false)}
          // Google prices live in Play Console, not our DB — only refresh those.
          onDone={async () => { setShowBulkPrice(false); clearSelection(); await loadGooglePrices() }}
        />
      )}

      {showBulkApplePrice && (
        <BulkApplePriceModal
          items={selectedItems}
          onClose={() => setShowBulkApplePrice(false)}
          // Apple prices live in App Store Connect, not our DB — only refresh those.
          onDone={async () => { setShowBulkApplePrice(false); clearSelection(); await loadApplePrices() }}
        />
      )}
    </div>
  )
}

// ── Modal: edit / delete / price ─────────────────────────────────────────────

function ItemModal ({ modal, onClose, onDone }) {
  const { mode, item } = modal

  if (mode === 'price') {
    return <PriceModal item={item} onClose={onClose} onDone={onDone} />
  }
  if (mode === 'applePrice') {
    return <ApplePriceModal item={item} onClose={onClose} onDone={onDone} />
  }
  if (mode === 'delete') {
    return <DeleteModal item={item} onClose={onClose} onDone={onDone} />
  }
  return <EditModal item={item} onClose={onClose} onDone={onDone} />
}

ItemModal.propTypes = {
  modal: PropTypes.shape({
    mode: PropTypes.oneOf(['edit', 'delete', 'price', 'applePrice']).isRequired,
    item: PropTypes.object.isRequired
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired
}

function useStatus () {
  const [status, setStatus] = useState({ text: '', kind: 'info' })
  const say = (text, kind = 'info') => setStatus({ text, kind })
  const cls = status.kind === 'err'
    ? classes.statusErr
    : status.kind === 'ok' ? classes.statusOk : classes.statusInfo
  return { status, say, cls }
}

function EditModal ({ item, onClose, onDone }) {
  const [name, setName] = useState(item.name || '')
  const [tags, setTags] = useState((item.tags || []).join(', '))
  const [price, setPrice] = useState(String(item.price ?? 0))
  const [flowerCost, setFlowerCost] = useState(item.flowerCost == null ? '' : String(item.flowerCost))
  const [freeTrialDays, setFreeTrialDays] = useState(item.freeTrialDays == null ? '' : String(item.freeTrialDays))
  const [freeDays, setFreeDays] = useState(() => (Array.isArray(item.freeOnDaysOfWeek) ? [...item.freeOnDaysOfWeek] : []))
  const toggleDay = (n) => setFreeDays((prev) => (prev.includes(n) ? prev.filter((d) => d !== n) : [...prev, n]).sort((a, b) => a - b))
  const [maxTrialDays, setMaxTrialDays] = useState(item.maxTrialDays == null ? '' : String(item.maxTrialDays))
  const [sortPriority, setSortPriority] = useState(String(item.sortPriority ?? 0))
  const [freeDownload, setFreeDownload] = useState(!!item.freeDownload)
  const [isVisible, setIsVisible] = useState(!!item.isVisible)
  const [busy, setBusy] = useState(false)
  const { status, say, cls } = useStatus()

  const save = async () => {
    setBusy(true)
    try {
      const patch = {
        name: name.trim(),
        tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
        price: Number(price) || 0,
        flowerCost: flowerCost.trim() === '' ? null : Number(flowerCost),
        freeTrialDays: freeTrialDays.trim() === '' ? null : Number(freeTrialDays),
        freeOnDaysOfWeek: [...freeDays].sort((a, b) => a - b),
        maxTrialDays: maxTrialDays.trim() === '' ? 0 : Number(maxTrialDays),
        sortPriority: Number(sortPriority) || 0,
        freeDownload,
        isVisible
      }
      say('Saving…')
      await updateLiveItem(item.itemId, patch)
      await onDone()
    } catch (err) {
      console.error(err)
      say(err.response?.data?.error || err.message, 'err')
      setBusy(false)
    }
  }

  return (
    <Shell busy={busy} onClose={onClose}>
      <h3 className={classes.modalTitle}>Edit {item.itemId}</h3>

      <div className={classes.field}>
        <span className={classes.label}>itemId (fixed)</span>
        <input className={`${classes.input} ${classes.inputReadonly}`} value={item.itemId} readOnly />
      </div>
      <div className={classes.field}>
        <span className={classes.label}>name</span>
        <input className={classes.input} value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className={classes.field}>
        <span className={classes.label}>tags (comma-separated)</span>
        <input className={classes.input} value={tags} onChange={(e) => setTags(e.target.value)} placeholder="anime, city" />
      </div>
      <div className={classes.field}>
        <span className={classes.label}>price (store, USD)</span>
        <input className={classes.input} type="number" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} />
      </div>
      <div className={classes.field}>
        <span className={classes.label}>flowerCost (花朵兌換數量，0 = 不可用花兌換)</span>
        <input className={classes.input} type="number" min="0" step="1" value={flowerCost} onChange={(e) => setFlowerCost(e.target.value)} />
      </div>
      <div className={classes.field}>
        <span className={classes.label}>freeTrialDays (免費試用天數，0 = 無 trial)</span>
        <input className={classes.input} type="number" min="0" step="1" value={freeTrialDays} onChange={(e) => setFreeTrialDays(e.target.value)} />
      </div>
      <div className={classes.field}>
        <span className={classes.label}>freeOnDaysOfWeek (每週免費日；可複選，未勾 = 無)</span>
        <div className={classes.controlCol}>
          <div className={classes.weekdayRow}>
            {WEEKDAYS.map((d) => (
              <label key={d.n}>
                <input type="checkbox" checked={freeDays.includes(d.n)} onChange={() => toggleDay(d.n)} />
                {d.n} {d.label}
              </label>
            ))}
          </div>
          {freeDays.length > 0 && (freeTrialDays.trim() === '' || Number(freeTrialDays) === 0) && (
            <span className={classes.warnText}>
              ⚠ 已設每週免費日，但 freeTrialDays = 0 → 解鎖會變成永久。限免 item 請一併設 freeTrialDays（例 7）。
            </span>
          )}
        </div>
      </div>
      <div className={classes.field}>
        <span className={classes.label}>maxTrialDays (試用倒數上限/進度條分母，0 = 用戶端預設 30)</span>
        <input className={classes.input} type="number" min="0" step="1" placeholder="30" value={maxTrialDays} onChange={(e) => setMaxTrialDays(e.target.value)} />
      </div>
      <div className={classes.field}>
        <span className={classes.label}>sortPriority</span>
        <input className={classes.input} type="number" value={sortPriority} onChange={(e) => setSortPriority(e.target.value)} />
      </div>
      <label className={classes.checkRow}>
        <input type="checkbox" checked={freeDownload} onChange={(e) => setFreeDownload(e.target.checked)} />
        <span className={classes.label}>freeDownload</span>
      </label>
      <label className={classes.checkRow}>
        <input type="checkbox" checked={isVisible} onChange={(e) => setIsVisible(e.target.checked)} />
        <span className={classes.label}>isVisible</span>
      </label>

      {status.text && <div className={`${classes.statusMsg} ${cls}`}>{status.text}</div>}
      <div className={classes.modalActions}>
        <button className={classes.btnGhost} onClick={onClose} disabled={busy}>取消</button>
        <button className={classes.btnPrimary} onClick={save} disabled={busy}>{busy ? 'Saving…' : '確定'}</button>
      </div>
    </Shell>
  )
}

// Bulk edit: pick which fields to change (checkbox on the left of each row), set a
// value, and apply it to EVERY selected item in one request. Unchecked fields are
// left untouched on all items.
function BulkEditModal ({ items, onClose, onDone }) {
  const [apply, setApply] = useState({})
  const toggleApply = (k) => setApply((p) => ({ ...p, [k]: !p[k] }))

  const [price, setPrice] = useState('')
  const [flowerCost, setFlowerCost] = useState('')
  const [freeTrialDays, setFreeTrialDays] = useState('')
  const [maxTrialDays, setMaxTrialDays] = useState('')
  const [sortPriority, setSortPriority] = useState('')
  const [tags, setTags] = useState('')
  const [freeDays, setFreeDays] = useState([])
  const [freeDownload, setFreeDownload] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const [busy, setBusy] = useState(false)
  const { status, say, cls } = useStatus()

  const toggleDay = (n) => setFreeDays((prev) => (prev.includes(n) ? prev.filter((d) => d !== n) : [...prev, n]).sort((a, b) => a - b))

  const save = async () => {
    const patch = {}
    if (apply.price) patch.price = Number(price) || 0
    if (apply.flowerCost) patch.flowerCost = flowerCost.trim() === '' ? null : Number(flowerCost)
    if (apply.freeTrialDays) patch.freeTrialDays = freeTrialDays.trim() === '' ? null : Number(freeTrialDays)
    if (apply.maxTrialDays) patch.maxTrialDays = maxTrialDays.trim() === '' ? 0 : Number(maxTrialDays)
    if (apply.sortPriority) patch.sortPriority = Number(sortPriority) || 0
    if (apply.tags) patch.tags = tags.split(',').map((t) => t.trim()).filter(Boolean)
    if (apply.freeOnDaysOfWeek) patch.freeOnDaysOfWeek = [...freeDays].sort((a, b) => a - b)
    if (apply.freeDownload) patch.freeDownload = freeDownload
    if (apply.isVisible) patch.isVisible = isVisible

    if (Object.keys(patch).length === 0) {
      say('請至少勾選一個要修改的欄位', 'err')
      return
    }

    setBusy(true)
    try {
      say('Applying…')
      const res = await bulkUpdateLiveItems(items.map((it) => it.itemId), patch)
      say(`已更新 ${res.modified}/${res.matched} 筆 · 欄位：${(res.fields || []).join(', ')}`, 'ok')
      setTimeout(() => { onDone() }, 1000)
    } catch (err) {
      console.error(err)
      say(err.response?.data?.error || err.message, 'err')
      setBusy(false)
    }
  }

  return (
    <Shell busy={busy} onClose={onClose}>
      <h3 className={classes.modalTitle}>Bulk edit · {items.length} item(s)</h3>
      <div className={classes.priceInfoBox}>
        勾選左側 checkbox 選擇「要修改的欄位」，只有被勾選的欄位會套用到全部 {items.length} 筆；未勾選的欄位維持各自原值。
      </div>

      <BulkNumberField label="price (USD)" k="price" apply={apply} toggleApply={toggleApply} value={price} setValue={setPrice} step="0.01" />
      <BulkNumberField label="flowerCost" k="flowerCost" apply={apply} toggleApply={toggleApply} value={flowerCost} setValue={setFlowerCost} />
      <BulkNumberField label="freeTrialDays" k="freeTrialDays" apply={apply} toggleApply={toggleApply} value={freeTrialDays} setValue={setFreeTrialDays} />
      <BulkNumberField label="maxTrialDays" k="maxTrialDays" apply={apply} toggleApply={toggleApply} value={maxTrialDays} setValue={setMaxTrialDays} placeholder="30" />
      <BulkNumberField label="sortPriority" k="sortPriority" apply={apply} toggleApply={toggleApply} value={sortPriority} setValue={setSortPriority} />

      <div className={classes.bulkField}>
        <label className={classes.bulkApply}>
          <input type="checkbox" checked={!!apply.tags} onChange={() => toggleApply('tags')} />
          tags
        </label>
        <input className={classes.input} value={tags} disabled={!apply.tags} onChange={(e) => setTags(e.target.value)} placeholder="anime, city（會覆蓋原 tags）" />
      </div>

      <div className={classes.bulkField}>
        <label className={classes.bulkApply}>
          <input type="checkbox" checked={!!apply.freeOnDaysOfWeek} onChange={() => toggleApply('freeOnDaysOfWeek')} />
          freeOnDaysOfWeek
        </label>
        <div className={classes.controlCol}>
          <div className={classes.weekdayRow}>
            {WEEKDAYS.map((d) => (
              <label key={d.n} style={apply.freeOnDaysOfWeek ? undefined : { opacity: 0.4 }}>
                <input type="checkbox" disabled={!apply.freeOnDaysOfWeek} checked={freeDays.includes(d.n)} onChange={() => toggleDay(d.n)} />
                {d.n} {d.label}
              </label>
            ))}
          </div>
          {apply.freeOnDaysOfWeek && freeDays.length > 0 && !apply.freeTrialDays && (
            <span className={classes.warnText}>
              ⚠ 設了每週免費日，建議一併勾選 freeTrialDays（例 7），否則解鎖會變永久。
            </span>
          )}
        </div>
      </div>

      <div className={classes.bulkField}>
        <label className={classes.bulkApply}>
          <input type="checkbox" checked={!!apply.freeDownload} onChange={() => toggleApply('freeDownload')} />
          freeDownload
        </label>
        <label className={classes.bulkValueCheck} style={apply.freeDownload ? undefined : { opacity: 0.4 }}>
          <input type="checkbox" disabled={!apply.freeDownload} checked={freeDownload} onChange={(e) => setFreeDownload(e.target.checked)} />
          設為 {freeDownload ? 'YES' : 'no'}
        </label>
      </div>

      <div className={classes.bulkField}>
        <label className={classes.bulkApply}>
          <input type="checkbox" checked={!!apply.isVisible} onChange={() => toggleApply('isVisible')} />
          isVisible
        </label>
        <label className={classes.bulkValueCheck} style={apply.isVisible ? undefined : { opacity: 0.4 }}>
          <input type="checkbox" disabled={!apply.isVisible} checked={isVisible} onChange={(e) => setIsVisible(e.target.checked)} />
          設為 {isVisible ? 'YES' : 'no'}
        </label>
      </div>

      {status.text && <div className={`${classes.statusMsg} ${cls}`}>{status.text}</div>}
      <div className={classes.modalActions}>
        <button className={classes.btnGhost} onClick={onClose} disabled={busy}>取消</button>
        <button className={classes.btnPrimary} onClick={save} disabled={busy}>{busy ? 'Applying…' : `套用到 ${items.length} 筆`}</button>
      </div>
    </Shell>
  )
}

BulkEditModal.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired
}

// One "apply checkbox + number input" row for the bulk editor.
function BulkNumberField ({ label, k, apply, toggleApply, value, setValue, step, placeholder }) {
  return (
    <div className={classes.bulkField}>
      <label className={classes.bulkApply}>
        <input type="checkbox" checked={!!apply[k]} onChange={() => toggleApply(k)} />
        {label}
      </label>
      <input
        className={classes.input}
        type="number"
        step={step || '1'}
        min="0"
        disabled={!apply[k]}
        placeholder={placeholder}
        value={value}
        onChange={(e) => setValue(e.target.value)}
      />
    </div>
  )
}

BulkNumberField.propTypes = {
  label: PropTypes.string.isRequired,
  k: PropTypes.string.isRequired,
  apply: PropTypes.object.isRequired,
  toggleApply: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
  setValue: PropTypes.func.isRequired,
  step: PropTypes.string,
  placeholder: PropTypes.string
}

// Bulk Google Play pricing: apply ONE base price to every selected SKU. The
// backend caps each request at BULK_CHUNK SKUs, so a bigger selection is sent as
// several sequential requests with a running progress message.
function BulkPriceModal ({ items, onClose, onDone }) {
  const { currency, setCurrency, prices, setPrice, raw } = useCurrencyPrices()
  const [name, setName] = useState('DIY LIVE WALLPAPER')
  const [description, setDescription] = useState('DIY LIVE WALLPAPER')
  const [createMissing, setCreateMissing] = useState(true)
  const [failures, setFailures] = useState([])
  const [busy, setBusy] = useState(false)
  const { status, say, cls } = useStatus()

  const itemIds = items.map((it) => it.itemId)

  const submit = async () => {
    const bad = priceError(raw, currency)
    if (bad) { say(bad, 'err'); return }
    if (createMissing && !name.trim()) { say('請輸入商品名稱（Name）', 'err'); return }
    if (createMissing && !description.trim()) { say('請輸入商品說明（Description）', 'err'); return }

    setBusy(true)
    setFailures([])
    const totals = { created: 0, updated: 0, skipped: 0, failed: 0 }
    const failed = []

    try {
      for (let i = 0; i < itemIds.length; i += BULK_CHUNK) {
        const chunk = itemIds.slice(i, i + BULK_CHUNK)
        say(`處理中… ${i}/${itemIds.length}`)
        const res = await bulkSetGooglePrice(chunk, {
          price: Number(raw),
          currency,
          name: name.trim(),
          description: description.trim(),
          createMissing
        })
        totals.created += res.created || 0
        totals.updated += res.updated || 0
        totals.skipped += res.skipped || 0
        totals.failed += res.failed || 0
        failed.push(...(res.results || []).filter((r) => !r.ok))
      }
    } catch (err) {
      // A whole chunk blew up (bad request / conversion failure) — stop here.
      console.error(err)
      say(err.response?.data?.error || err.message, 'err')
      setFailures(failed)
      setBusy(false)
      return
    }

    const summary = `已更新 ${totals.updated} 筆 · 新建 ${totals.created} 筆` +
      (totals.skipped ? ` · 略過 ${totals.skipped} 筆（Play Console 尚無此商品）` : '') +
      (totals.failed ? ` · 失敗 ${totals.failed} 筆` : '')

    if (totals.failed > 0) {
      // Keep the modal open so the admin can see exactly which SKUs to retry.
      setFailures(failed)
      say(summary, 'err')
      setBusy(false)
      return
    }
    say(summary, 'ok')
    setTimeout(() => { onDone() }, 1000)
  }

  return (
    <Shell busy={busy} onClose={onClose}>
      <h3 className={classes.modalTitle}>Bulk Google price · {items.length} item(s)</h3>
      <div className={classes.priceInfoBox}>
        下方價格會套用到全部 {items.length} 個 itemId。基準幣別為 {currency}：
        {CURRENCIES[currency].symbol} 金額原封套用到該幣別所屬地區，其他國家由 Google 自動換算。
        {items.length > BULK_CHUNK && <><br />共 {Math.ceil(items.length / BULK_CHUNK)} 批送出，每批 {BULK_CHUNK} 筆。</>}
      </div>

      <BasePriceField
        currency={currency}
        setCurrency={setCurrency}
        prices={prices}
        setPrice={setPrice}
        autoFocus
      />

      <label className={classes.checkRow}>
        <input type="checkbox" checked={createMissing} onChange={(e) => setCreateMissing(e.target.checked)} />
        <span className={classes.label}>Play Console 尚無的 itemId 自動建立</span>
      </label>

      {createMissing && (
        <>
          <div className={classes.field}>
            <span className={classes.label}>Name *</span>
            <input
              className={classes.input}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="商品名稱（Google Play 標題）"
              maxLength={55}
            />
            <span className={classes.hint}>僅用於新建的商品；已存在的商品只改價格，標題不動。</span>
          </div>
          <div className={classes.field}>
            <span className={classes.label}>Description *</span>
            <input
              className={classes.input}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="商品說明（Google Play 必填）"
              maxLength={200}
            />
            <span className={classes.hint}>Purchase option ID 由各自 itemId 自動推導（_ → -）。</span>
          </div>
        </>
      )}

      {failures.length > 0 && (
        <ul className={classes.bulkResultList}>
          {failures.map((f) => (
            <li key={f.productId}><b>{f.productId}</b> — {f.error}</li>
          ))}
        </ul>
      )}

      {status.text && <div className={`${classes.statusMsg} ${cls}`}>{status.text}</div>}
      <div className={classes.modalActions}>
        <button className={classes.btnGhost} onClick={onClose} disabled={busy}>
          {failures.length > 0 ? '關閉' : '取消'}
        </button>
        <button className={classes.btnPrimary} onClick={submit} disabled={busy}>
          {busy ? 'Working…' : `套用到 ${items.length} 筆`}
        </button>
      </div>
    </Shell>
  )
}

BulkPriceModal.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired
}

function DeleteModal ({ item, onClose, onDone }) {
  const [busy, setBusy] = useState(false)
  const { status, say, cls } = useStatus()

  const doDelete = async () => {
    setBusy(true)
    try {
      say('Deleting…')
      await deleteLiveItem(item.itemId)
      await onDone()
    } catch (err) {
      console.error(err)
      say(err.response?.data?.error || err.message, 'err')
      setBusy(false)
    }
  }

  return (
    <Shell busy={busy} onClose={onClose}>
      <h3 className={classes.modalTitle}>Delete {item.itemId}?</h3>
      <p style={{ color: '#b7c0cd', fontSize: '0.88rem' }}>
        This removes the live wallpaper record{item.thumbnailBlob ? ' and its thumbnail file' : ''}. This cannot be undone.
      </p>
      {status.text && <div className={`${classes.statusMsg} ${cls}`}>{status.text}</div>}
      <div className={classes.modalActions}>
        <button className={classes.btnGhost} onClick={onClose} disabled={busy}>取消</button>
        <button className={classes.btnDanger} onClick={doDelete} disabled={busy}>{busy ? 'Deleting…' : 'Delete'}</button>
      </div>
    </Shell>
  )
}

// Currency selector + base price input, shared by the single and bulk modals.
// Keeps one value PER currency so switching the selector never discards typing.
function BasePriceField ({ currency, setCurrency, prices, setPrice, autoFocus }) {
  const cur = CURRENCIES[currency]
  return (
    <div className={`${classes.field} ${classes.fieldStacked}`}>
      <span className={classes.label}>Base price</span>
      <div className={classes.priceInputWrap}>
        <select
          className={classes.currencySelect}
          value={currency}
          onChange={(e) => setCurrency(e.target.value)}
          aria-label="Base currency"
        >
          {CURRENCY_CODES.map((code) => (
            <option key={code} value={code}>{code} {CURRENCIES[code].symbol}</option>
          ))}
        </select>
        <input
          className={classes.input}
          style={{ flex: 1 }}
          type="number"
          step={cur.step}
          min="0"
          value={prices[currency]}
          onChange={(e) => setPrice(currency, e.target.value)}
          placeholder={cur.placeholder}
          autoFocus={autoFocus}
        />
      </div>
      <span className={classes.hint}>
        例：輸入 {cur.placeholder} → {cur.homeLabel}商店顯示 {cur.symbol}{cur.placeholder}（含稅價，使用者實付金額）
        {cur.decimals === 0 && '。整數，不接受小數'}
      </span>
    </div>
  )
}
BasePriceField.propTypes = {
  currency: PropTypes.string.isRequired,
  setCurrency: PropTypes.func.isRequired,
  prices: PropTypes.object.isRequired,
  setPrice: PropTypes.func.isRequired,
  autoFocus: PropTypes.bool
}

// One price value per currency, so switching the selector preserves both.
function useCurrencyPrices () {
  const [currency, setCurrency] = useState('USD')
  const [prices, setPrices] = useState(
    () => Object.fromEntries(CURRENCY_CODES.map((c) => [c, '']))
  )
  const setPrice = (code, value) => setPrices((prev) => ({ ...prev, [code]: value }))
  return { currency, setCurrency, prices, setPrices, setPrice, raw: prices[currency] }
}

function PriceModal ({ item, onClose, onDone }) {
  const [loading, setLoading] = useState(true)
  const [exists, setExists] = useState(false)
  const { currency, setCurrency, prices, setPrices, setPrice, raw } = useCurrencyPrices()
  const [name, setName] = useState('DIY LIVE WALLPAPER')
  const [description, setDescription] = useState('DIY LIVE WALLPAPER')
  // Google's purchaseOptionId disallows underscores; seed from itemId (_ -> -).
  const [purchaseOptionId, setPurchaseOptionId] = useState(item.itemId.replace(/_/g, '-'))
  const [busy, setBusy] = useState(false)
  const { status, say, cls } = useStatus()

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const data = await getGooglePrice(item.itemId)
        if (cancelled) return
        setExists(!!data.exists)
        // Prefill every currency so switching the selector shows the live price.
        setPrices(Object.fromEntries(
          CURRENCY_CODES.map((c) => [c, data.prices?.[c] != null ? String(data.prices[c]) : ''])
        ))
      } catch (err) {
        if (!cancelled) say(err.response?.data?.error || err.message, 'err')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [item.itemId])

  const submit = async () => {
    const bad = priceError(raw, currency)
    if (bad) { say(bad, 'err'); return }
    if (!exists && !name.trim()) { say('請輸入商品名稱（Name）', 'err'); return }
    if (!exists && !description.trim()) { say('請輸入商品說明（Description）', 'err'); return }
    if (!exists && !purchaseOptionId.trim()) { say('請輸入 Purchase option ID', 'err'); return }
    setBusy(true)
    try {
      say(exists ? 'Updating price…' : 'Creating one-time product…')
      const res = await setGooglePrice(item.itemId, Number(raw), currency, name.trim(), description.trim(), purchaseOptionId.trim())
      say(`${res.created ? '已新增' : '已更新'} · ${res.regions} 個地區已自動換算幣值`, 'ok')
      setTimeout(() => { onDone() }, 900)
    } catch (err) {
      console.error(err)
      say(err.response?.data?.error || err.message, 'err')
      setBusy(false)
    }
  }

  return (
    <Shell busy={busy} onClose={onClose}>
      <h3 className={classes.modalTitle}>Update Google price</h3>
      <div className={classes.field}>
        <span className={classes.label}>itemId (SKU)</span>
        <input className={`${classes.input} ${classes.inputReadonly}`} value={item.itemId} readOnly />
      </div>

      {loading
        ? <div className={classes.loading}>讀取 Google Play 價格…</div>
        : (
        <>
          <div className={classes.priceInfoBox}>
            {exists
              ? '此 itemId 在 Google Play 已有 one-time purchase，可編輯下方價格後按「確定」更新。'
              : '此 itemId 在 Google Play 尚無 one-time purchase，按「確定」將以此價格新增一個項目。'}
            <br />基準幣別為 {currency}：{CURRENCIES[currency].symbol} 金額會原封套用到該幣別的所屬地區，其他國家由 Google 依此自動換算。
          </div>

          {!exists && (
            <>
              <div className={classes.field}>
                <span className={classes.label}>Name *</span>
                <input
                  className={classes.input}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="商品名稱（Google Play 標題）"
                  maxLength={55}
                />
                <span className={classes.hint}>Google Play 商品標題（title），最多 55 字。</span>
              </div>
              <div className={classes.field}>
                <span className={classes.label}>Description *</span>
                <input
                  className={classes.input}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="商品說明（Google Play 必填）"
                  maxLength={200}
                />
                <span className={classes.hint}>Google Play 要求 one-time product 必須填寫說明。</span>
              </div>
              <div className={classes.field}>
                <span className={classes.label}>Purchase option ID *</span>
                <input
                  className={classes.input}
                  value={purchaseOptionId}
                  onChange={(e) => setPurchaseOptionId(e.target.value)}
                  placeholder="purchase-option-id"
                />
                <span className={classes.hint}>預設由 itemId 底線轉連字號（_ → -），可自行修改。</span>
              </div>
            </>
          )}

          <BasePriceField
            currency={currency}
            setCurrency={setCurrency}
            prices={prices}
            setPrice={setPrice}
            autoFocus
          />
        </>
          )}

      {status.text && <div className={`${classes.statusMsg} ${cls}`}>{status.text}</div>}
      <div className={classes.modalActions}>
        <button className={classes.btnGhost} onClick={onClose} disabled={busy}>取消</button>
        <button className={classes.btnPrimary} onClick={submit} disabled={busy || loading}>
          {busy ? 'Working…' : '確定'}
        </button>
      </div>
    </Shell>
  )
}

// Apple accepts positive USD only; the exact amount then snaps to the closest
// Apple price point on the backend.
function applePriceError (raw) {
  const n = Number(raw)
  if (raw === '' || !Number.isFinite(n) || n <= 0) {
    return '請輸入有效的美金價格，例如 1.99'
  }
  return null
}

// Everything the admin should know before an Apple write, shared by the single
// and bulk modals.
function AppleInfoNote ({ count }) {
  return (
    <div className={classes.priceInfoBox}>
      Apple 只接受官方價格點（無法任意定價）：輸入的 USD 金額會自動吸附到最接近的檔位，
      套用後回報實際價格。美國價為基準，其他國家由 Apple 自動換算。
      {count != null && <><br />共 {count} 筆，超過 {APPLE_BULK_CHUNK} 筆會分批送出。</>}
      <br />⚠ 新建立的商品（product id = diy_itemId）會停在「Ready to Submit」，
      首次需隨 app 版本在 App Store Connect 送審一次；之後改價不用再送審。
    </div>
  )
}
AppleInfoNote.propTypes = { count: PropTypes.number }

// Apple display-name / description inputs used only when a product is being
// created (ASC requires an en-US localization before submission).
function AppleCreateFields ({ name, setName, description, setDescription }) {
  return (
    <>
      <div className={classes.field}>
        <span className={classes.label}>Display name *</span>
        <input
          className={classes.input}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="使用者看到的商品名稱"
          maxLength={30}
        />
        <span className={classes.hint}>App Store 購買彈窗顯示的名稱，最多 30 字。</span>
      </div>
      <div className={classes.field}>
        <span className={classes.label}>Description *</span>
        <input
          className={classes.input}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="商品說明"
          maxLength={45}
        />
        <span className={classes.hint}>App Store 商品說明，最多 45 字。僅用於新建；已存在的商品只改價格。</span>
      </div>
    </>
  )
}
AppleCreateFields.propTypes = {
  name: PropTypes.string.isRequired,
  setName: PropTypes.func.isRequired,
  description: PropTypes.string.isRequired,
  setDescription: PropTypes.func.isRequired
}

function ApplePriceModal ({ item, onClose, onDone }) {
  const [loading, setLoading] = useState(true)
  const [exists, setExists] = useState(false)
  const [state, setState] = useState(null) // ASC state, e.g. READY_TO_SUBMIT
  const [price, setPrice] = useState('')
  const [name, setName] = useState('DIY LIVE WALLPAPER')
  const [description, setDescription] = useState('Unlock watermark-free export')
  const [busy, setBusy] = useState(false)
  const { status, say, cls } = useStatus()

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const data = await getApplePrice(item.itemId)
        if (cancelled) return
        setExists(!!data.exists)
        setState(data.state || null)
        if (data.prices?.USD != null) setPrice(String(data.prices.USD))
      } catch (err) {
        if (!cancelled) say(err.response?.data?.error || err.message, 'err')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [item.itemId])

  const submit = async () => {
    const bad = applePriceError(price)
    if (bad) { say(bad, 'err'); return }
    if (!exists && !name.trim()) { say('請輸入 Display name', 'err'); return }
    if (!exists && !description.trim()) { say('請輸入 Description', 'err'); return }
    setBusy(true)
    try {
      say(exists ? 'Updating price…' : 'Creating in-app purchase…')
      const res = await setApplePrice(item.itemId, Number(price), name.trim(), description.trim())
      const snapped = res.appliedPrice !== res.requestedPrice
        ? `（輸入 $${res.requestedPrice} → 吸附到 $${res.appliedPrice}）`
        : ''
      say(`${res.created ? '已新增' : '已更新'} · 實際價格 $${res.appliedPrice}${snapped}`, 'ok')
      setTimeout(() => { onDone() }, 1200)
    } catch (err) {
      console.error(err)
      say(err.response?.data?.error || err.message, 'err')
      setBusy(false)
    }
  }

  return (
    <Shell busy={busy} onClose={onClose}>
      <h3 className={classes.modalTitle}>Update Apple price</h3>
      <div className={classes.field}>
        <span className={classes.label}>Product ID</span>
        <input className={`${classes.input} ${classes.inputReadonly}`} value={item.itemId} readOnly />
      </div>

      {loading
        ? <div className={classes.loading}>讀取 App Store 價格…</div>
        : (
        <>
          <div className={classes.priceInfoBox}>
            {exists
              ? `此商品已存在於 App Store Connect${state ? `（狀態：${state}）` : ''}，按「確定」更新價格。`
              : '此 itemId 在 App Store Connect 尚無 in-app purchase，按「確定」將自動建立（非消耗性商品）。'}
          </div>
          <AppleInfoNote />

          {!exists && (
            <AppleCreateFields
              name={name} setName={setName}
              description={description} setDescription={setDescription}
            />
          )}

          <div className={`${classes.field} ${classes.fieldStacked}`}>
            <span className={classes.label}>USD price</span>
            <input
              className={classes.input}
              type="number"
              step="0.01"
              min="0"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="1.99"
              autoFocus
            />
            <span className={classes.hint}>例：輸入 1.99 → 美國商店顯示 $1.99（未含稅地區的稅在結帳時另計）</span>
          </div>
        </>
          )}

      {status.text && <div className={`${classes.statusMsg} ${cls}`}>{status.text}</div>}
      <div className={classes.modalActions}>
        <button className={classes.btnGhost} onClick={onClose} disabled={busy}>取消</button>
        <button className={classes.btnPrimary} onClick={submit} disabled={busy || loading}>
          {busy ? 'Working…' : '確定'}
        </button>
      </div>
    </Shell>
  )
}

// Bulk Apple pricing: apply ONE USD price to every selected itemId. Chunked at
// APPLE_BULK_CHUNK per request with a running progress message.
function BulkApplePriceModal ({ items, onClose, onDone }) {
  const [price, setPrice] = useState('')
  const [name, setName] = useState('DIY LIVE WALLPAPER')
  const [description, setDescription] = useState('Unlock watermark-free export')
  const [createMissing, setCreateMissing] = useState(true)
  const [failures, setFailures] = useState([])
  const [busy, setBusy] = useState(false)
  const { status, say, cls } = useStatus()

  const itemIds = items.map((it) => it.itemId)

  const submit = async () => {
    const bad = applePriceError(price)
    if (bad) { say(bad, 'err'); return }
    if (createMissing && !name.trim()) { say('請輸入 Display name', 'err'); return }
    if (createMissing && !description.trim()) { say('請輸入 Description', 'err'); return }

    setBusy(true)
    setFailures([])
    const totals = { created: 0, updated: 0, skipped: 0, failed: 0 }
    const failed = []

    try {
      for (let i = 0; i < itemIds.length; i += APPLE_BULK_CHUNK) {
        const chunk = itemIds.slice(i, i + APPLE_BULK_CHUNK)
        say(`處理中… ${i}/${itemIds.length}`)
        const res = await bulkSetApplePrice(chunk, {
          price: Number(price),
          name: name.trim(),
          description: description.trim(),
          createMissing
        })
        totals.created += res.created || 0
        totals.updated += res.updated || 0
        totals.skipped += res.skipped || 0
        totals.failed += res.failed || 0
        failed.push(...(res.results || []).filter((r) => !r.ok))
      }
    } catch (err) {
      // A whole chunk blew up (bad request / credentials) — stop here.
      console.error(err)
      say(err.response?.data?.error || err.message, 'err')
      setFailures(failed)
      setBusy(false)
      return
    }

    const summary = `已更新 ${totals.updated} 筆 · 新建 ${totals.created} 筆` +
      (totals.skipped ? ` · 略過 ${totals.skipped} 筆（ASC 尚無此商品）` : '') +
      (totals.failed ? ` · 失敗 ${totals.failed} 筆` : '')

    if (totals.failed > 0) {
      // Keep the modal open so the admin can see exactly which SKUs to retry.
      setFailures(failed)
      say(summary, 'err')
      setBusy(false)
      return
    }
    say(summary, 'ok')
    setTimeout(() => { onDone() }, 1000)
  }

  return (
    <Shell busy={busy} onClose={onClose}>
      <h3 className={classes.modalTitle}>Bulk Apple price · {items.length} item(s)</h3>
      <AppleInfoNote count={items.length} />

      <div className={`${classes.field} ${classes.fieldStacked}`}>
        <span className={classes.label}>USD price</span>
        <input
          className={classes.input}
          type="number"
          step="0.01"
          min="0"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          placeholder="1.99"
          autoFocus
        />
      </div>

      <label className={classes.checkRow}>
        <input type="checkbox" checked={createMissing} onChange={(e) => setCreateMissing(e.target.checked)} />
        <span className={classes.label}>App Store Connect 尚無的 itemId 自動建立</span>
      </label>

      {createMissing && (
        <AppleCreateFields
          name={name} setName={setName}
          description={description} setDescription={setDescription}
        />
      )}

      {failures.length > 0 && (
        <ul className={classes.bulkResultList}>
          {failures.map((f) => (
            <li key={f.productId}><b>{f.productId}</b> — {f.error}</li>
          ))}
        </ul>
      )}

      {status.text && <div className={`${classes.statusMsg} ${cls}`}>{status.text}</div>}
      <div className={classes.modalActions}>
        <button className={classes.btnGhost} onClick={onClose} disabled={busy}>
          {failures.length > 0 ? '關閉' : '取消'}
        </button>
        <button className={classes.btnPrimary} onClick={submit} disabled={busy}>
          {busy ? 'Working…' : `套用到 ${items.length} 筆`}
        </button>
      </div>
    </Shell>
  )
}

BulkApplePriceModal.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired
}

// Shared modal shell (overlay + panel).
function Shell ({ busy, onClose, children }) {
  return (
    <div className={classes.overlay} onClick={busy ? undefined : onClose}>
      <div className={classes.modal} onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  )
}

const itemShape = PropTypes.object
EditModal.propTypes = { item: itemShape.isRequired, onClose: PropTypes.func.isRequired, onDone: PropTypes.func.isRequired }
DeleteModal.propTypes = { item: itemShape.isRequired, onClose: PropTypes.func.isRequired, onDone: PropTypes.func.isRequired }
PriceModal.propTypes = { item: itemShape.isRequired, onClose: PropTypes.func.isRequired, onDone: PropTypes.func.isRequired }
ApplePriceModal.propTypes = { item: itemShape.isRequired, onClose: PropTypes.func.isRequired, onDone: PropTypes.func.isRequired }
Shell.propTypes = { busy: PropTypes.bool, onClose: PropTypes.func.isRequired, children: PropTypes.node }

export default withAdminAuth(LiveWallpaperManager)
